import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { useAuth } from '../stores/auth';
import {
  ArrowLeft,
  MapPin,
  Send,
  Camera,
  AlertCircle,
  Lock,
  Calendar,
  Wrench,
  HelpCircle,
  Store,
} from 'lucide-react';

interface StoreInfo {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  contact_name?: string;
  contact_phone?: string;
}

type JustificationReason = 'CLOSED' | 'NO_ACCESS' | 'HOLIDAY' | 'RENOVATION' | 'OTHER';

interface ReasonOption {
  value: JustificationReason;
  label: string;
  icon: React.ReactNode;
}

const REASON_OPTIONS: ReasonOption[] = [
  { value: 'CLOSED', label: 'Fechada', icon: <Store className="h-5 w-5" /> },
  { value: 'NO_ACCESS', label: 'Sem Acesso', icon: <Lock className="h-5 w-5" /> },
  { value: 'HOLIDAY', label: 'Feriado', icon: <Calendar className="h-5 w-5" /> },
  { value: 'RENOVATION', label: 'Em Reforma', icon: <Wrench className="h-5 w-5" /> },
  { value: 'OTHER', label: 'Outro', icon: <HelpCircle className="h-5 w-5" /> },
];

export default function JustifyNoVisit({ storeId, routeId }: { storeId: string; routeId?: string }) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [reason, setReason] = useState<JustificationReason | null>(null);
  const [notes, setNotes] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Fetch store information
  const { data: store, isLoading: isLoadingStore } = useQuery<StoreInfo>({
    queryKey: ['store', storeId],
    queryFn: () => api.get(`/stores/${storeId}`).then(res => res.data.data),
  });

  // Submit justification mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!reason) throw new Error('Selecione um motivo');
      if (reason === 'OTHER' && !notes.trim()) throw new Error('Informe a observacao');

      const payload: Record<string, unknown> = {
        store_id: storeId,
        reason,
        notes: notes.trim() || undefined,
      };

      if (routeId) {
        payload.route_id = routeId;
      }

      if (user?.id) {
        payload.promoter_id = user.id;
      }

      if (photoUrl) {
        payload.photo_url = photoUrl;
      }

      const response = await api.post('/justifications', payload);
      return response.data.data;
    },
    onSuccess: () => {
      setLocation('/route');
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || error.message || 'Erro ao enviar justificativa');
    },
  });

  const handleSubmit = () => {
    if (!reason) {
      alert('Selecione um motivo para a nao-visita');
      return;
    }

    if (reason === 'OTHER' && !notes.trim()) {
      alert('Informe uma observacao quando o motivo for "Outro"');
      return;
    }

    submitMutation.mutate();
  };

  const handlePhotoCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append('photo', file, file.name);
        formData.append('photo_type', 'JUSTIFICATION');
        const response = await api.post('/photos/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPhotoUrl(response.data?.data?.url || URL.createObjectURL(file));
      } catch {
        // Fallback: use local object URL if upload fails
        setPhotoUrl(URL.createObjectURL(file));
      }
    };
    input.click();
  };

  if (isLoadingStore) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Loja nao encontrada</h2>
        <button
          onClick={() => setLocation('/route')}
          className="mt-4 px-6 py-2 bg-primary text-white rounded-lg"
        >
          Voltar ao roteiro
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/route')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Justificar Nao-Visita</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 space-y-4">
        {/* Store Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-start gap-3">
            <div className="p-3 rounded-full bg-primary/10">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">{store.name}</h2>
              <p className="text-sm text-gray-600 mt-1">{store.address}</p>
              <p className="text-sm text-gray-500">{store.city} - {store.state}</p>
            </div>
          </div>
        </div>

        {/* Reason Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Motivo
          </h3>
          <div className="space-y-2">
            {REASON_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setReason(option.value)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${
                  reason === option.value
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-gray-100 bg-white text-gray-700 active:bg-gray-50'
                }`}
              >
                <div className={`p-2 rounded-full ${
                  reason === option.value ? 'bg-primary/10' : 'bg-gray-100'
                }`}>
                  {option.icon}
                </div>
                <span className="font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Observacao {reason === 'OTHER' && <span className="text-red-500">*</span>}
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={reason === 'OTHER' ? 'Descreva o motivo da nao-visita...' : 'Informacoes adicionais (opcional)...'}
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
          />
          {reason === 'OTHER' && !notes.trim() && (
            <p className="text-xs text-red-500 mt-1">Observacao obrigatoria quando o motivo for "Outro"</p>
          )}
        </div>

        {/* Photo Capture (Optional) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Foto (opcional)
          </h3>
          {photoUrl ? (
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <Camera className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-700 font-medium flex-1">Foto anexada</span>
              <button
                onClick={() => setPhotoUrl(null)}
                className="text-sm text-red-500 font-medium"
              >
                Remover
              </button>
            </div>
          ) : (
            <button
              onClick={handlePhotoCapture}
              className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 active:bg-gray-50"
            >
              <Camera className="h-5 w-5" />
              <span className="font-medium">Tirar Foto</span>
            </button>
          )}
        </div>

        {/* Error Message */}
        {submitMutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">
                {(submitMutation.error as any)?.response?.data?.error?.message ||
                  (submitMutation.error as any)?.message ||
                  'Erro ao enviar justificativa'}
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Fixed Submit Button */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
        <button
          onClick={handleSubmit}
          disabled={!reason || submitMutation.isPending || (reason === 'OTHER' && !notes.trim())}
          className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Enviando...</span>
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              <span>Enviar Justificativa</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
